import { zodResolver } from "@hookform/resolvers/zod";
import { UseMutationResult } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { Loader2, Plus as AddIcon } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useTenantSchema } from "@/components/forms/schemas/tenantSchema";
import { Button } from "@/components/ui/v2/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/v2/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/v2/form";
import { Input } from "@/components/ui/v2/input";
import { AddTenantRequest } from "@/types/request/AddTenantRequest";
import { AddTenantResponse } from "@/types/response/AddTenantResponse";
import { ValidationResponse } from "@/types/response/ValidationResponse";

interface AddTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  addTenant: UseMutationResult<
    AxiosResponse<AddTenantResponse>,
    AxiosError,
    AddTenantRequest
  >;
}

export function AddTenantDialog({
  open,
  onOpenChange,
  onSuccess,
  addTenant,
}: AddTenantDialogProps) {
  const { t } = useTranslation();
  const { tenantSchema } = useTenantSchema();
  const { mutate, isPending, error: tenantError, reset: resetMutation } =
    addTenant;

  const form = useForm<AddTenantRequest>({
    resolver: zodResolver(tenantSchema),
    defaultValues: {
      name: "",
    },
  });

  const { handleSubmit, setError, clearErrors } = form;

  useEffect(() => {
    if (!tenantError?.response?.data || !Array.isArray(tenantError.response.data))
      return;
    const error = (tenantError.response.data as ValidationResponse).find(
      (e) => e.propertyName === "name"
    );
    if (error) {
      const fieldName = t("superAdmin.tenant.tenantName");
      setError("name", {
        message: t(`errorCodes.${error.errorMessage}`, {
          fieldName: t(`${fieldName}`),
        }),
      });
    }
  }, [tenantError, setError, t]);

  const onSubmit = (data: AddTenantRequest) => {
    mutate(data, {
      onSuccess: () => {
        onSuccess();
        onOpenChange(false);
      },
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen || !isPending) onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        onEscapeKeyDown={(e) => {
          if (isPending) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (isPending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t("superAdmin.tenant.addTenant")}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("superAdmin.tenant.tenantName")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t("superAdmin.tenant.tenantName")}
                      autoFocus
                      onChange={(e) => {
                        field.onChange(e);
                        resetMutation();
                        clearErrors();
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="pt-8">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <AddIcon className="size-4" />
                )}
                {t("superAdmin.tenant.addTenant")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
